<script setup lang="ts">
import { computed } from "vue";
import { just, nothing } from "../../lib/maybe";
import ZwMenu from "./ZwMenu.vue";
import ZwMenuItem from "./ZwMenuItem.vue";

export type ZwDropdownOption = {
  value: string;
  label: string;
};

const props = withDefaults(
  defineProps<{
    /** Muted name on the chip (e.g. Mode, Weight). */
    label: string;
    options: readonly ZwDropdownOption[];
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const model = defineModel<string>({ required: true });

const selectedLabel = computed(() => {
  const match = props.options.find((opt) => opt.value === model.value);
  if (typeof match !== "undefined") return match.label;
  return model.value;
});

const menuLabel = computed(() => just(props.label));

function select(value: string): void {
  model.value = value;
}
</script>

<template>
  <ZwMenu align="start" :menu-label="menuLabel" :disabled="disabled">
    <template #trigger="{ toggle, attrs, open }">
      <button
        type="button"
        class="zw-dropdown-trigger"
        :class="{ 'zw-dropdown-trigger-open': open }"
        :disabled="disabled"
        :aria-label="`${label}: ${selectedLabel}`"
        v-bind="attrs"
        @click="toggle()"
      >
        <span class="zw-dropdown-name">{{ label }}</span>
        <span class="zw-dropdown-value">{{ selectedLabel }}</span>
        <svg
          class="zw-dropdown-caret"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path
            d="M2.2 3.6 5 6.4 7.8 3.6"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </template>
    <ZwMenuItem
      v-for="option in options"
      :key="option.value"
      :label="option.label"
      :shortcut="nothing"
      :title="nothing"
      @select="select(option.value)"
    />
  </ZwMenu>
</template>

<style>
.zw-dropdown-trigger {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.85rem;
  padding: 0 0.45rem 0 0.55rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text);
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
}

.zw-dropdown-trigger:hover:not(:disabled) {
  background: var(--hover-fill);
}

.zw-dropdown-trigger:disabled {
  opacity: 0.4;
  cursor: default;
}

.zw-dropdown-trigger-open {
  border-color: color-mix(in oklch, var(--comment) 55%, var(--line));
  background: var(--hover-fill);
  color: var(--text-strong);
}

.zw-dropdown-name {
  color: var(--text-muted);
  font-size: 0.75rem;
  letter-spacing: 0.01em;
}

.zw-dropdown-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-strong);
  min-width: 2.25rem;
  text-align: right;
}

.zw-dropdown-caret {
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform var(--duration-fast, 150ms)
    var(--ease-out-quart, cubic-bezier(0.25, 1, 0.5, 1));
}

.zw-dropdown-trigger-open .zw-dropdown-caret {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .zw-dropdown-caret {
    transition: none;
  }
}
</style>
